import {
  workerBatchQueryResponseBodySchema,
  workerQueryResponseBodySchema,
} from "#worker-query-schema";
import type { WorkerQuerySend } from "#worker-query-executor";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { boundedPromise, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Coalesce concurrently-issued worker reads into a single `POST /query/batch`.
 *
 * WHY: on web every content/static read is a network round-trip to the worker, and that round-trip
 * costs the same (~110ms of latency) whether the statement returns one row or sixty-four — the cost
 * is per-REQUEST, not per-row. A screen that issues a dozen small independent reads therefore pays
 * ~12x the latency for what is really one round-trip's worth of work. Collapsing everything issued
 * within the same tick into one request removes that multiplier outright.
 *
 * This wraps the transport rather than living at a query layer deliberately: `WorkerQuerySend` is
 * the single chokepoint every remote read already funnels through (including the param-chunked
 * statements `executeWorkerQuery` produces), so batching here benefits every call site — existing
 * and future — without any of them changing.
 */

/** Largest number of statements sent in one batch. MUST stay <= the worker's `MAX_BATCH_QUERIES`. */
const MAX_BATCH_SIZE = 32;

/**
 * One worker POST, headers AND the JSON body. Every web static/content read funnels through here,
 * and before this bound a connection that was accepted but never answered (captive-portal Wi-Fi, a
 * wedged Worker isolate) suspended the screen that issued the read with no error and no retry.
 * 15s matches the per-attempt timeout of the server/native transport (`static-database.server.ts`):
 * a D1 read answers in ~100ms, so 15s is only ever reached by a request that is not coming back.
 */
const WORKER_QUERY_DEADLINE_MS = 15_000;

/**
 * How long a read may wait for its batch to answer: the batch POST plus, when the worker predates
 * `/query/batch` and 404s it, the individual replay of that read. Both are already bounded by
 * {@link WORKER_QUERY_DEADLINE_MS}, so this is a backstop over the whole hand-off (the microtask
 * flush, the batch, the replay), never the bound that normally fires.
 */
const BATCHED_QUERY_DEADLINE_MS = 2 * WORKER_QUERY_DEADLINE_MS + 5000;

type WorkerPostResult =
  | { body: unknown; ok: true }
  | { detail: string; ok: false; status: number; statusText: string };

/**
 * POST `payload` to the worker and read the reply, all within {@link WORKER_QUERY_DEADLINE_MS}.
 * The body is read INSIDE the deadline on purpose: a server that sends headers and then stalls the
 * body is as hung as one that never answers.
 */
async function postToWorker(
  url: string,
  payload: unknown,
  operation: string,
): Promise<WorkerPostResult> {
  return withDeadline({ deadline: WORKER_QUERY_DEADLINE_MS, operation }, async (signal) => {
    const response = await fetch(url, {
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      method: "POST",
      signal,
    });
    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      return { detail, ok: false, status: response.status, statusText: response.statusText };
    }
    const body: unknown = await response.json();
    return { body, ok: true };
  });
}

type PendingQuery = {
  body: Parameters<WorkerQuerySend>[0];
  reject: (error: unknown) => void;
  resolve: (rows: unknown) => void;
};

/**
 * Flush on a microtask rather than a timer or animation frame.
 *
 * A microtask captures everything issued in the current synchronous tick — which is exactly the
 * burst a `Promise.all` over independent reads produces — while still resolving in the same tick
 * from the caller's perspective. A frame-aligned flush (~16ms) would coalesce more aggressively but
 * would ALSO delay a lone read by a frame, and these reads sit on the critical path of first paint.
 * A microtask adds no measurable delay to the single-query case.
 */
function scheduleFlush(flush: () => void): void {
  void Promise.resolve().then(flush);
}

/**
 * Build a batching {@link WorkerQuerySend} for one endpoint.
 *
 * `sendSingle` is the existing unbatched transport, retained as the fallback for the cases where
 * batching cannot help or is not supported: a solitary query (nothing to coalesce with) and a
 * worker that does not yet serve `/query/batch`.
 */
export function createBatchedWorkerQuerySend({
  batchUrl,
  label,
  sendSingle,
}: {
  batchUrl: string;
  label: string;
  sendSingle: WorkerQuerySend;
}): WorkerQuerySend {
  let queue: PendingQuery[] = [];
  let flushScheduled = false;
  // Set once the worker 404s `/query/batch`, so a deployment whose worker predates the batch
  // endpoint degrades to the previous one-request-per-read behaviour instead of failing outright.
  let batchEndpointUnavailable = false;

  async function sendBatch(batch: PendingQuery[]): Promise<void> {
    // Nothing to coalesce — the batch request would be pure overhead over the plain endpoint.
    const solitary = batch.length === 1 ? batch[0] : undefined;
    if (solitary !== undefined) {
      try {
        solitary.resolve(await sendSingle(solitary.body));
      } catch (error) {
        solitary.reject(error);
      }
      return;
    }

    try {
      const response = await postToWorker(
        batchUrl,
        { queries: batch.map((pending) => pending.body) },
        `${label} batch query`,
      );

      if (!response.ok && response.status === 404) {
        // Worker predates the batch endpoint. Latch it off and replay this batch individually so
        // the request still succeeds; subsequent reads skip straight to the single-query path.
        batchEndpointUnavailable = true;
        await Promise.all(batch.map(async (pending) => replaySingle(pending)));
        return;
      }

      if (!response.ok) {
        throw new Error(
          `${label} batch query failed (${response.status} ${response.statusText}): ${response.detail}`,
        );
      }

      const { results } = workerBatchQueryResponseBodySchema.parse(response.body);
      if (arrayLength(results) !== arrayLength(batch)) {
        throw new Error(
          `${label} batch query returned ${arrayLength(results)} results for ${arrayLength(batch)} queries`,
        );
      }

      for (const [index, pending] of batch.entries()) {
        const result = results[index];
        if (result === undefined) {
          pending.reject(new Error(`${label} batch query returned no result at index ${index}`));
          continue;
        }
        if ("error" in result) {
          // Per-statement failure: reject only the caller that issued it.
          pending.reject(new Error(`${label} query failed: ${result.error}`));
          continue;
        }
        // `rows` is passed through UNCHANGED — for `get` it is the single row or `null`, and
        // coercing that `null` to `[]` would make drizzle's RQB-v2 `mapGetResult` run
        // `JSON.parse("")` and throw. See the transport modules for the full writeup.
        pending.resolve(result.rows);
      }
    } catch (error) {
      for (const pending of batch) {
        pending.reject(error);
      }
    }
  }

  async function replaySingle(pending: PendingQuery): Promise<void> {
    try {
      pending.resolve(await sendSingle(pending.body));
    } catch (error) {
      pending.reject(error);
    }
  }

  function flush(): void {
    flushScheduled = false;
    const pendingQueue = queue;
    queue = [];
    // Respect the worker's per-request cap by splitting an oversized tick into several batches.
    for (let offset = 0; offset < arrayLength(pendingQueue); offset += MAX_BATCH_SIZE) {
      void sendBatch(pendingQueue.slice(offset, offset + MAX_BATCH_SIZE));
    }
  }

  return async (body) => {
    if (batchEndpointUnavailable) {
      return sendSingle(body);
    }
    return boundedPromise<unknown>(
      { deadline: BATCHED_QUERY_DEADLINE_MS, operation: `${label} batched query` },
      (resolve, reject) => {
        queue.push({ body, reject, resolve });
        if (!flushScheduled) {
          flushScheduled = true;
          scheduleFlush(flush);
        }
      },
    );
  };
}

/** Shared single-query transport used as both the batch fallback and the non-batched path. */
export function createSingleWorkerQuerySend({
  label,
  queryUrl,
}: {
  label: string;
  queryUrl: string;
}): WorkerQuerySend {
  return async ({ method, params, rowMode, sql }) => {
    const response = await postToWorker(
      queryUrl,
      { method, params, rowMode, sql },
      `${label} query`,
    );
    if (!response.ok) {
      throw new Error(
        `${label} worker query failed (${response.status} ${response.statusText}): ${response.detail}`,
      );
    }
    return workerQueryResponseBodySchema.parse(response.body).rows;
  };
}
